import { useCallback, useMemo, useState } from 'react';
import {
  UnorderedListOutlined,
  PlayCircleOutlined,
  ShareAltOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useNotificationContext } from '@/features/notifications';
import { ProcShareModal } from '@/features/proc-management';
import {
  ProcDetailsResponse,
  useProcDuplicate,
  useProcInstantiate,
  useProcPublish,
} from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { captureError } from '@/shared/utils/error-reporting';
import {
  RegenerateProcModal,
  UnsavedChangesModal,
} from '@/widgets/proc-edit-view';

type Props = {
  proc?: ProcDetailsResponse;
  changesSaved?: boolean;
  setChangesSaved?: (changesSaved: boolean) => void;
  resetLogicCount?: number;
  setResetLogicCount?: (resetLogicCount: number) => void;
};

export const ProcActions = ({
  proc,
  changesSaved,
  setChangesSaved,
  resetLogicCount,
  setResetLogicCount,
}: Props) => {
  const { groupId = '', id = '' } = proc || {};
  const navigate = useNavigate();
  const invalidate = useDataInvalidation(['procs', proc?.id]);
  const { showNotification } = useNotificationContext();
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [regenerateProcModalOpen, setRegenerateProcModalOpen] = useState(false);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);

  const isPublishedVersion = proc?.state === 'published';
  const isPublished = !!proc?.baseProcId || isPublishedVersion;
  const isProcPublishable = useMemo(
    () => proc?.allowedActions?.includes('publish'),
    [proc],
  );

  const { mutate: publishProc, isLoading: publishProcLoading } =
    useProcPublish();
  const { mutate: editProc, isLoading: editLoading } = useProcDuplicate();
  const { mutate: instantiateProc, isLoading: instantiateLoading } =
    useProcInstantiate();

  const onPublish = useCallback(() => {
    if (changesSaved) {
      publishProc(
        { pathParams: { id: proc?.id || '' }, body: { isPublic: false } },
        {
          onSuccess: () => {
            showNotification({
              type: 'success',
              message: 'Proc published',
              description:
                'The proc is successfully published and ready for use.',
            });
            invalidate();
          },
          onError: (error) => {
            showNotification({
              type: 'error',
              message: 'Proc publish error',
              description: error.message,
            });
            captureError(error, {
              module: 'proc',
              action: 'publish',
            });
          },
        },
      );
    } else {
      setUnsavedChangesModalOpen(true);
    }
  }, [changesSaved, invalidate, proc?.id, publishProc, showNotification]);

  const handleEditProc = useCallback(() => {
    editProc(
      {
        pathParams: { id },
        queryParams: { linkToBase: true },
      },
      {
        onSuccess: ({ id }) => {
          navigate(`/dashboard/author/proc/${id}`, { replace: true });
        },
        onError: (error) => {
          if (error.message) {
            showNotification({
              type: 'error',
              message: 'Proc edit error',
              description: error.message,
            });
          }
          captureError(error, {
            module: 'proc_preview_page_header_on_error_handle_edit_proc',
            action: 'handle_edit_proc',
          });
        },
      },
    );
  }, [editProc, id, navigate, showNotification]);

  const handleProcInstantiate = useCallback(() => {
    instantiateProc(
      { pathParams: { id: groupId } },
      {
        onSuccess: ({ instanceId }) => {
          navigate(`/dashboard/proc/instance/${instanceId}`);
        },
        onError: (error) => {
          if (error.message) {
            showNotification({
              type: 'error',
              message: 'Proc instantiate error',
              description: error.message,
            });
            captureError(error, {
              module:
                'proc_preview_page_header_on_error_handle_proc_instantiate',
              action: 'handle_proc_instantiate',
            });
          }
        },
      },
    );
  }, [groupId, instantiateProc, navigate, showNotification]);

  return (
    <>
      {!isPublishedVersion && (
        <Tooltip
          title={
            !isProcPublishable &&
            'You are unable to publish this proc as there are are some empty steps. Either delete those or add any component to.'
          }
        >
          <Button
            type="primary"
            disabled={!isProcPublishable}
            loading={publishProcLoading}
            onClick={onPublish}
            style={{ marginRight: 10 }}
          >
            Publish
          </Button>
        </Tooltip>
      )}
      {!isPublishedVersion && (
        <Tooltip
          title={
            isProcPublishable
              ? 'Regenerate proc'
              : 'You are unable to regenerate this proc as there are are some empty steps. Either delete those or add any component to.'
          }
        >
          <Button
            type="text"
            shape="circle"
            icon={<RedoOutlined />}
            onClick={() => setRegenerateProcModalOpen(true)}
          />
        </Tooltip>
      )}
      {isPublishedVersion && (
        <Button
          type="primary"
          loading={editLoading}
          onClick={handleEditProc}
          style={{ marginRight: 10 }}
        >
          Edit
        </Button>
      )}
      {isPublishedVersion && (
        <Tooltip title="Preview">
          <Button
            type="text"
            shape="circle"
            disabled={instantiateLoading}
            icon={<PlayCircleOutlined />}
            onClick={() => handleProcInstantiate()}
          />
        </Tooltip>
      )}
      {isPublished && (
        <Tooltip title="Share proc">
          <Button
            type="text"
            shape="circle"
            icon={<ShareAltOutlined />}
            onClick={() => setShareModalVisible(true)}
          />
        </Tooltip>
      )}
      {isPublished && (
        <Tooltip title="Go to instances">
          <Button
            type="text"
            shape="circle"
            icon={<UnorderedListOutlined />}
            onClick={() =>
              navigate(`/dashboard/author/proc/${proc?.id}/instances`)
            }
          />
        </Tooltip>
      )}
      <ProcShareModal
        open={shareModalVisible}
        procGroupId={groupId}
        onCancel={() => setShareModalVisible(false)}
      />
      <RegenerateProcModal
        procId={proc?.id}
        open={regenerateProcModalOpen}
        onCancel={() => setRegenerateProcModalOpen(false)}
      />
      <UnsavedChangesModal
        open={unsavedChangesModalOpen}
        onConfirm={() => {
          setChangesSaved?.(true);
          setResetLogicCount?.(resetLogicCount! + 1);
          setUnsavedChangesModalOpen(false);
          onPublish();
        }}
        onCancel={() => setUnsavedChangesModalOpen(false)}
      />
    </>
  );
};
